import { useState, useEffect } from 'react';
import { AppConstants } from '@/app.constants';
import { idbStorage } from '@/store/idbStorage/idbStorage';
import { formatBytes, computeStringBytes } from '@/utils/bytes.util';

interface DBInfo {
  origin: string;
  bucketName: string;
  isPersistent: boolean;
  durability: string;
  quota: string;
  expiration: string;
  version: number;
  objectStores: number;
}

export enum IndexedDBDataType {
  Image,
  Inspection,
}
interface TableData {
  key: string;
  value: string;
  type?: IndexedDBDataType;
}

interface IndexedDBData {
  dbInfo: DBInfo;
  imagesData: TableData[];
  inspectionsData: TableData[];
  metaData: TableData[];
  savedInspectionsData: TableData[];
  openedInspectionData: TableData[];
  locationVerificationDraftsData: TableData[];
  factoryVisitSessionsData: TableData[];
  openSyncLogsData: TableData[];
  pendingLogsData: TableData[];
  imagesSize: string;
  inspectionsSize: string;
  metaDataSize: string;
  savedInspectionsSize: string;
  openedInspectionSize: string;
  locationVerificationDraftsSize: string;
  factoryVisitSessionsSize: string;
  openSyncLogsSize: string;
  pendingLogsSize: string;
  idbQuota: string;
  idbUsage: string;
  loading: boolean;
  error: string | null;
}

interface IndexedDBInfo extends IndexedDBData {
  refresh: () => Promise<void>;
}

const EMPTY_TABLE_DATA: TableData[] = [];

export const useIndexedDBInfo = (): IndexedDBInfo => {
  const [data, setData] = useState<IndexedDBData>({
    dbInfo: {
      origin: '',
      bucketName: 'default',
      isPersistent: false,
      durability: 'relaxed',
      quota: '0 B',
      expiration: 'None',
      version: AppConstants.INDEX_DB.version,
      objectStores: Object.keys(AppConstants.INDEX_DB.store).length,
    },
    imagesData: EMPTY_TABLE_DATA,
    inspectionsData: EMPTY_TABLE_DATA,
    metaData: EMPTY_TABLE_DATA,
    savedInspectionsData: EMPTY_TABLE_DATA,
    openedInspectionData: EMPTY_TABLE_DATA,
    locationVerificationDraftsData: EMPTY_TABLE_DATA,
    factoryVisitSessionsData: EMPTY_TABLE_DATA,
    openSyncLogsData: EMPTY_TABLE_DATA,
    pendingLogsData: EMPTY_TABLE_DATA,
    imagesSize: '0 B',
    inspectionsSize: '0 B',
    metaDataSize: '0 B',
    savedInspectionsSize: '0 B',
    openedInspectionSize: '0 B',
    locationVerificationDraftsSize: '0 B',
    factoryVisitSessionsSize: '0 B',
    openSyncLogsSize: '0 B',
    pendingLogsSize: '0 B',
    idbQuota: '0 B',
    idbUsage: '0 B',
    loading: true,
    error: null,
  });

  const computeValueBytes = (value: unknown): number => {
    try {
      const str = typeof value === 'string' ? value : JSON.stringify(value);
      return computeStringBytes(str);
    } catch {
      return 0;
    }
  };

  const getPersistentStatus = async (): Promise<boolean> => {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        return await navigator.storage.persisted();
      } catch (error) {
        console.warn('Could not get persistent status:', error);
        return false;
      }
    }
    return false;
  };

  const fetchDBInfo = async (): Promise<void> => {
    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      const isPersistent = await getPersistentStatus();
      const origin = window.location.origin;
      const stores = AppConstants.INDEX_DB.store;

      const [
        imagesData,
        inspectionsData,
        masterData,
        savedInspectionsData,
        openedInspectionData,
        locationVerificationDraftsData,
        factoryVisitSessionsData,
        openSyncLogsData,
        pendingLogsData,
      ] = await Promise.all([
        idbStorage.getAll(stores.images),
        idbStorage.getAllKeys(stores.inspections),
        idbStorage.getAll(stores.masterData),
        idbStorage.getAll(stores.savedInspections),
        idbStorage.getAll(stores.openedInspection),
        idbStorage.getAll(stores.locationVerificationDrafts),
        idbStorage.getAll(stores.factoryVisitSessions),
        idbStorage.getAll(stores.openSyncLogs),
        idbStorage.getAll(stores.pendingLogs),
      ]);

      const [
        imagesKeys,
        inspectionsKeys,
        masterDataKeys,
        savedInspectionsKeys,
        openedInspectionKeys,
        locationVerificationDraftsKeys,
        factoryVisitSessionsKeys,
        openSyncLogsKeys,
        pendingLogsKeys,
      ] = await Promise.all([
        idbStorage.getAllKeys(stores.images),
        idbStorage.getAllKeys(stores.inspections),
        idbStorage.getAllKeys(stores.masterData),
        idbStorage.getAllKeys(stores.savedInspections),
        idbStorage.getAllKeys(stores.openedInspection),
        idbStorage.getAllKeys(stores.locationVerificationDrafts),
        idbStorage.getAllKeys(stores.factoryVisitSessions),
        idbStorage.getAllKeys(stores.openSyncLogs),
        idbStorage.getAllKeys(stores.pendingLogs),
      ]);

      const [
        imagesValues,
        inspectionsValues,
        masterValues,
        savedInspectionsValues,
        openedInspectionValues,
        locationVerificationDraftsValues,
        factoryVisitSessionsValues,
        openSyncLogsValues,
        pendingLogsValues,
      ] = await Promise.all([
        idbStorage.getAll(stores.images),
        idbStorage.getAll(stores.inspections),
        idbStorage.getAll(stores.masterData),
        idbStorage.getAll(stores.savedInspections),
        idbStorage.getAll(stores.openedInspection),
        idbStorage.getAll(stores.locationVerificationDrafts),
        idbStorage.getAll(stores.factoryVisitSessions),
        idbStorage.getAll(stores.openSyncLogs),
        idbStorage.getAll(stores.pendingLogs),
      ]);

      const sumBytes = (values: unknown[]): number =>
        (values || []).reduce((sum: number, value: unknown) => sum + computeValueBytes(value), 0);

      const imagesUsedBytes = sumBytes(imagesValues);
      const inspectionsUsedBytes = sumBytes(inspectionsValues);
      const masterUsedBytes = sumBytes(masterValues);
      const savedInspectionsUsedBytes = sumBytes(savedInspectionsValues);
      const openedInspectionUsedBytes = sumBytes(openedInspectionValues);
      const locationVerificationDraftsUsedBytes = sumBytes(locationVerificationDraftsValues);
      const factoryVisitSessionsUsedBytes = sumBytes(factoryVisitSessionsValues);
      const openSyncLogsUsedBytes = sumBytes(openSyncLogsValues);
      const pendingLogsUsedBytes = sumBytes(pendingLogsValues);

      const usedBytes =
        imagesUsedBytes +
        inspectionsUsedBytes +
        masterUsedBytes +
        savedInspectionsUsedBytes +
        openedInspectionUsedBytes +
        locationVerificationDraftsUsedBytes +
        factoryVisitSessionsUsedBytes +
        openSyncLogsUsedBytes +
        pendingLogsUsedBytes;
      const quota = formatBytes(usedBytes);

      let estimatedQuota = 0;
      let estimatedUsage = 0;
      if (navigator?.storage?.estimate) {
        try {
          const estimate = await navigator.storage.estimate();
          estimatedQuota = typeof estimate.quota === 'number' ? estimate.quota : 0;
          estimatedUsage = typeof estimate.usage === 'number' ? estimate.usage : 0;
        } catch {}
      }

      const convertStoreToTableData = (
        keys: unknown[],
        storeData: unknown[],
        dataType?: IndexedDBDataType
      ): TableData[] => {
        const tableData: TableData[] = [];
        keys.forEach((key, index) => {
          const keyStr = typeof key === 'string' ? key : String(key);
          let valueStr = 'Data';
          if (storeData[index]) {
            try {
              const value =
                typeof storeData[index] === 'string'
                  ? storeData[index]
                  : JSON.stringify(storeData[index]);
              valueStr = value;
            } catch {
              valueStr = 'Complex data';
            }
          }
          tableData.push({
            key: keyStr,
            value: valueStr,
            type: dataType,
          });
        });
        return tableData;
      };

      setData({
        dbInfo: {
          origin,
          bucketName: 'default',
          isPersistent,
          durability: 'relaxed',
          quota,
          expiration: 'None',
          version: AppConstants.INDEX_DB.version,
          objectStores: Object.keys(AppConstants.INDEX_DB.store).length,
        },
        imagesData: convertStoreToTableData(imagesKeys, imagesData, IndexedDBDataType.Image),
        inspectionsData: convertStoreToTableData(inspectionsKeys, inspectionsData),
        metaData: convertStoreToTableData(masterDataKeys, masterData),
        savedInspectionsData: convertStoreToTableData(
          savedInspectionsKeys,
          savedInspectionsData,
          IndexedDBDataType.Inspection
        ),
        openedInspectionData: convertStoreToTableData(openedInspectionKeys, openedInspectionData),
        locationVerificationDraftsData: convertStoreToTableData(
          locationVerificationDraftsKeys,
          locationVerificationDraftsData
        ),
        factoryVisitSessionsData: convertStoreToTableData(
          factoryVisitSessionsKeys,
          factoryVisitSessionsData
        ),
        openSyncLogsData: convertStoreToTableData(openSyncLogsKeys, openSyncLogsData),
        pendingLogsData: convertStoreToTableData(pendingLogsKeys, pendingLogsData),
        imagesSize: formatBytes(imagesUsedBytes),
        inspectionsSize: formatBytes(inspectionsUsedBytes),
        metaDataSize: formatBytes(masterUsedBytes),
        savedInspectionsSize: formatBytes(savedInspectionsUsedBytes),
        openedInspectionSize: formatBytes(openedInspectionUsedBytes),
        locationVerificationDraftsSize: formatBytes(locationVerificationDraftsUsedBytes),
        factoryVisitSessionsSize: formatBytes(factoryVisitSessionsUsedBytes),
        openSyncLogsSize: formatBytes(openSyncLogsUsedBytes),
        pendingLogsSize: formatBytes(pendingLogsUsedBytes),
        idbQuota: formatBytes(estimatedQuota),
        idbUsage: formatBytes(estimatedUsage),
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching IndexedDB info:', error);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch database info',
      }));
    }
  };

  useEffect(() => {
    fetchDBInfo();
  }, []);

  return {
    ...data,
    refresh: fetchDBInfo,
  };
};
